import React, {useState, useEffect, useCallback} from 'react';
import moment from 'moment';
import isEmpty from 'lodash/isEmpty';
import filter from 'lodash/filter';

import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';

import useDateInterval from '../../hooks/useDateInterval';
import useToggle from '../../hooks/useToggle';
import DateIntervalPicker from '../../components/Forms/DateIntervalPicker';
import {withData} from '../../containers/DataContainer';
import NewButton from '../../components/Buttons/NewButton'
import EditButton from '../../components/Buttons/EditButton'
import FilteredList from '../../components/Lists/FilteredList';
import TwoColumns from '../../components/Grids/TwoColumns';
import {DeliveriesTableComponent, DeliveriesDetailComponent} from '../Deliveries';
import useStyles from '../styles';

import Form from './Form';
import Stats from './Stats';
import ConfirmModal from './Confirm';

const Meta = withData(({
  data,
  fetchMeta,
  fetchDeliveriesByDate,
  createMeta,
  updateMeta,
  deleteMeta,
}) => {

  const {meta} = data;

  const classes = useStyles();

  // root states
  const [path, setPath] = useState('form');
  const [selectedMeta, _setMeta] = useState({});
  const [rowData, setRowData] = useState({});
  const [isMetaBusy, setMetaBusy] = useState(false);
  const [isDeliveryBusy, setDeliveryBusy] = useState(false);
  const [isDetailBusy, setDetailBusy] = useState(false);
  const [isModalOpen, toggleModal] = useToggle(false);
  const [startDate, setStartDate, endDate, setEndDate] = useDateInterval({
    startDate: moment().startOf('day'),
    endDate: moment().endOf('day')
  });

  const clearSelectedMeta = useCallback(() => _setMeta({}), [_setMeta]);

  const setSelectedMeta = useCallback(id => _setMeta(
    filter(meta, el => el._id === id)[0]
  ), [meta]);

  useEffect(() => {
    const fetchData = async () => {
      setMetaBusy(true);
      await fetchMeta();
      setMetaBusy(false);
    };
    fetchData();
  }, [fetchMeta]);

  const fetchVehicleData = useCallback(async () => {
    setDeliveryBusy(true);
    await fetchDeliveriesByDate({
      startDate: '' + startDate.valueOf(),
      endDate: '' + Math.max(endDate.valueOf(), startDate.valueOf())
    });
    setDeliveryBusy(false);
  }, [startDate, endDate, fetchDeliveriesByDate]);

  useEffect(() => {
    fetchVehicleData();
  }, [fetchVehicleData]);

  const _updateMeta = state => {
    updateMeta(state);
    clearSelectedMeta();
  };

  const _deleteMeta = () => {
    deleteMeta({_id: selectedMeta._id});
    clearSelectedMeta();
    toggleModal();
  };

  const onItemClick = useCallback(rowData => async () => {
    setPath('data');
    setSelectedMeta(rowData);
    // await fetchVehicleData();
  }, [setPath, setSelectedMeta]);

  const toggleNewForm = useCallback(() => {
    clearSelectedMeta();
    setPath('form');
  }, [clearSelectedMeta, setPath]);

  const MetaListHeader = useCallback(() => <Grid
    container
    justify='space-between'
    alignItems='flex-start'
    className={classes.title}
  >
    <Typography variant='h6'>
      Veículos
    </Typography>
    {!isEmpty(selectedMeta) &&
    <NewButton onClick={toggleNewForm}/>
    }
  </Grid>, [toggleNewForm, selectedMeta, classes.title]);

  const MetaList = useCallback(() => <FilteredList
    data={meta}
    isBusy={isMetaBusy}
    header={<MetaListHeader/>}
    {...{onItemClick}}
  />, [meta, isMetaBusy, onItemClick]);

  const MetaDeliveries = () => <Grid
    container
    direction='row'
  >
    <Grid container className={classes.stats} spacing={2}>
      <Grid item xs={12}>
        <DateIntervalPicker
          {...{startDate, setStartDate, endDate, setEndDate}}
        />
      </Grid>
      <Grid item xs={12}>
        <Grid container justify='space-between'>
          <Grid item xs={11}>
            <Stats
              meta={selectedMeta}
              dateData={{startDate, setStartDate, endDate, setEndDate}}
            />
          </Grid>
          <Grid item xs={1}>
            <EditButton onClick={() => setPath('form')}/>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
    <Grid item xs={12}>
      <DeliveriesTableComponent
        meta={selectedMeta}
        height={'65vh'}
        {...{
          path,
          setPath,
          setRowData,
          setDetailBusy,
          isDeliveryBusy
        }}
      />
    </Grid>
  </Grid>;

  const Switch = () => {
    if (['data', 'detail', 'delete', 'update'].includes(path))
      return <MetaDeliveries/>;
    else if (path === 'form')
      return <Form
        data={selectedMeta}
        onUpdate={_updateMeta}
        onCreate={createMeta}
        onDelete={toggleModal}
        onClose={() => setPath('data')}
      />;
    return null;
  };

  return <>
    <TwoColumns
      left={<MetaList/>}
      right={<Switch/>}
    />
    <ConfirmModal
      isOpen={isModalOpen}
      onClose={toggleModal}
      onSubmit={_deleteMeta}
      metaId={selectedMeta._id}
    />
    <DeliveriesDetailComponent
      path={['form', 'data'].includes(path) ? 'root' : path}
      setPath={path => path === 'root' ? setPath('data') : setPath(path)}
      fetchData={fetchVehicleData}
      {...{
        rowData,
        setDetailBusy,
        isDetailBusy
      }}
    />
  </>;
});

export default React.memo(Meta);
